import { useEffect, useMemo, useState } from 'react';

import { getCandidateAiReviewStateFromCandidate } from '../candidate-ai/candidateAiReviewState';
import { CandidateAiReviewStateType, CandidateType } from '../candidates/Candidates.types';

type UseCandidateAiReviewStateSourceProps = {
  candidate?: CandidateType;
  polledAiReviewState?: CandidateAiReviewStateType;
  isAiRefreshSessionActive: boolean;
};

export const useCandidateAiReviewStateSource = ({
  candidate,
  polledAiReviewState,
  isAiRefreshSessionActive,
}: UseCandidateAiReviewStateSourceProps) => {
  const canonicalAiReviewState = useMemo(
    () => (candidate ? getCandidateAiReviewStateFromCandidate(candidate) : undefined),
    [candidate]
  );
  const [latestAiReviewState, setLatestAiReviewState] = useState<CandidateAiReviewStateType | null>(null);

  useEffect(() => {
    setLatestAiReviewState(null);
  }, [candidate?.uuid]);

  useEffect(() => {
    if (!polledAiReviewState) {
      return;
    }

    setLatestAiReviewState(polledAiReviewState);
  }, [polledAiReviewState]);

  useEffect(() => {
    if (!canonicalAiReviewState || isAiRefreshSessionActive || polledAiReviewState) {
      return;
    }

    setLatestAiReviewState(canonicalAiReviewState);
  }, [canonicalAiReviewState, isAiRefreshSessionActive, polledAiReviewState]);

  const effectiveAiReviewState = latestAiReviewState ?? canonicalAiReviewState;

  const candidateWithLatestAiState = useMemo(() => {
    if (!candidate || !effectiveAiReviewState) {
      return candidate;
    }

    return {
      ...candidate,
      aiSuggestions: effectiveAiReviewState.aiSuggestions,
      cvProcessing: effectiveAiReviewState.cvProcessing,
    };
  }, [candidate, effectiveAiReviewState]);

  return {
    effectiveAiReviewState,
    candidateWithLatestAiState,
  };
};
